import { isLogoutFlowInProgress } from 'components/core/WithLogout/model';
import { runWhenSwConfigReady } from 'services/serviceWorker/swConfigReady';
import { logInfo } from 'setup/logger/logInfo';
import { isCancelledRequest, isChannelRequestTimeout, type ChannelUnsubscribe } from 'shared/channels';
import { isQueuedMutationPending } from 'shared/channels/entity';
import { isFailUploadedMessage, type Message } from 'shared/domain/messages/message';
import { showFailUploadErrorToast } from 'components/core/UploadProgress/showFailUploadErrorToast';
import type { DataActions } from '../types';
import { classifyEntityEvent } from './model';
import {
  EntityEventReaction,
  type EntityCollectionActions,
  type EntityCollectionConfig,
  type RelatedEntityClient,
} from './types';

/**
 * Store actions of a collection served by an entity client, next to
 * `createDataActions` (whose `setItems` / `setLoading` / `reset` they use
 * through `getDataActions`). Like the documentation store, the store loads and
 * follows the worker's events itself; `useEntityCollectionInitializer` only
 * binds these actions to the mounted app. The returned actions are created
 * once per store and keep their identity (also across `reset()`), so hooks
 * can depend on them in effects.
 */
export function createEntityCollectionActions<TModel>(
  { client, entityName, reloadOnChangesOf = [], onLoadFailed, skipReloadAfter }: EntityCollectionConfig<TModel>,
  getDataActions: () => DataActions<TModel>
): EntityCollectionActions<TModel> {
  /** Bumped when the collection opens or closes: reads started before are dropped. Not state, so outside it. */
  let generation = 0;

  const reload = async (): Promise<TModel[] | undefined> => {
    // Covers every caller, also forms reloading after a save.
    if (isLogoutFlowInProgress()) {
      return undefined;
    }
    const startedIn = generation;
    try {
      const items = await client.getAll();
      if (startedIn === generation) {
        getDataActions().setItems(items);
      }
      return items;
    } catch (error) {
      // A timeout is already reported with diagnostics by the channel layer, and a cancelled read failed at nothing.
      if (!isChannelRequestTimeout(error) && !isCancelledRequest(error)) {
        logInfo('entity-collection-load-failed', { entity: entityName, error });
      }
      if (startedIn === generation) {
        getDataActions().setLoading(false);
        onLoadFailed?.();
      }
      return undefined;
    }
  };

  const requestReload = (): void => {
    runWhenSwConfigReady(() => void reload());
  };

  const reloadAfterChange = (event: Message): void => {
    if (!skipReloadAfter?.(event)) {
      requestReload();
    }
  };

  /**
   * A failed change nobody waits for (its command timed out, it was queued
   * before a reload or in another tab) is shown here; a pending command's
   * caller shows its own failure.
   */
  const reportUnawaitedFailure = (event: Message): void => {
    if (event.error && isFailUploadedMessage(event) && !isQueuedMutationPending(event.uniqueId)) {
      showFailUploadErrorToast(event, `Error while uploading ${entityName}`);
    }
  };

  const reloadAfterChangesOf = ({ onEvent, readCollection }: RelatedEntityClient): ChannelUnsubscribe =>
    onEvent((event) => {
      if (classifyEntityEvent(event, readCollection).reaction === EntityEventReaction.reload) {
        reloadAfterChange(event);
      }
    });

  return {
    reload,
    requestReload,

    subscribeToChannelMessages: (): ChannelUnsubscribe => {
      const stopEvents = client.onEvent((event) => {
        reportUnawaitedFailure(event);
        const outcome = classifyEntityEvent(event, client.readCollection);
        switch (outcome.reaction) {
          case EntityEventReaction.applyCollection:
            getDataActions().setItems(outcome.items);
            return;
          case EntityEventReaction.reload:
            reloadAfterChange(event);
            return;
          case EntityEventReaction.stopLoading:
            getDataActions().setLoading(false);
            onLoadFailed?.();
            return;
          case EntityEventReaction.ignore:
            return;
        }
      });
      const stopRelatedEvents = reloadOnChangesOf.map(reloadAfterChangesOf);
      return () => {
        stopEvents();
        stopRelatedEvents.forEach((stop) => stop());
      };
    },

    openCollection: () => {
      generation += 1;
      getDataActions().setLoading(true);
      requestReload();
      return () => {
        generation += 1;
        getDataActions().reset();
      };
    },
  };
}
